import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { ErrorBox } from "../support/ErrorBox";
import { FormikProps } from "formik";
import InputLabel from "@mui/material/InputLabel";
import { MuiPhone } from "../../phone-input/MuiPhone";
import TextField from "@mui/material/TextField";
import { CustomerAccessData as Type } from "../customer-model";
import Typography from "@mui/material/Typography";
import { useTranslation } from "next-i18next";

interface Props {
  formik: FormikProps<Type>;
}

/**
 * Customer Access step / screen.
 */
export function CustomerInformationStep({ formik }: Props) {
  const { t } = useTranslation();

  return (
    <Box>
      <Box
        sx={{
          marginBottom: "20px",
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: "1.75em",
            fontWeight: "bold",
          }}
        >
          {t("common:common.request_access")}
        </Typography>
      </Box>
      <Box sx={{ fontSize: "14px", mt: "24px", mb: "12px", textAlign: "left" }}>
        {t("request_access:customer_access.contact_information")}
      </Box>
      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            marginBottom: "20px",
          }}
        >
          {/* First Name */}
          <InputLabel
            sx={{ textAlign: "left", mt: "1rem" }}
            htmlFor="firstName"
          >
            {t("common:common.first_name")}
          </InputLabel>
          <TextField
            fullWidth
            id="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.firstName && formik.touched.firstName && (
            <ErrorBox>{formik.errors.firstName}</ErrorBox>
          )}

          {/* Last Name */}
          <InputLabel sx={{ textAlign: "left", mt: "1rem" }} htmlFor="lastName">
            {t("common:common.last_name")}
          </InputLabel>
          <TextField
            fullWidth
            id="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.lastName && formik.touched.lastName && (
            <ErrorBox>{formik.errors.lastName}</ErrorBox>
          )}

          {/* Customer Role*/}
          <InputLabel sx={{ textAlign: "left", mt: "1rem" }} htmlFor="role">
            {t("request_access:customer_access.customer_role")}
          </InputLabel>
          <TextField
            fullWidth
            id="role"
            value={formik.values.role}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.role && formik.touched.role && (
            <ErrorBox>{formik.errors.role}</ErrorBox>
          )}

          {/* Email */}
          <InputLabel sx={{ textAlign: "left", mt: "1rem" }} htmlFor="email">
            {t("common:common.email_address")}
          </InputLabel>
          <TextField
            fullWidth
            id="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.email && formik.touched.email && (
            <ErrorBox>{formik.errors.email}</ErrorBox>
          )}

          {/* Phone Number */}
          <InputLabel
            sx={{ textAlign: "left", mt: "1rem" }}
            htmlFor="phoneNumber"
          >
            {t("common:common.phone_number")}
          </InputLabel>
          <MuiPhone
            fullWidth
            id="phoneNumber"
            name="phoneNumber"
            value={formik.values.phoneNumber}
            onChange={(phone, iso2) => {
              formik.setFieldValue("phoneIso", iso2, true);
              formik.setFieldValue("phoneNumber", phone, true);
            }}
            onBlur={formik.handleBlur}
          />
          {formik.errors.phoneNumber && formik.touched.phoneNumber && (
            <ErrorBox>{formik.errors.phoneNumber}</ErrorBox>
          )}
        </Box>
        <Box mt={12}>
          <Button
            sx={{ width: "100%", mb: 4 }}
            type="submit"
            disabled={!(formik.dirty && formik.isValid)}
          >
            {t("common:common.continue")}
          </Button>
        </Box>
      </form>
    </Box>
  );
}